import { useRollbar } from "@rollbar/react";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { useRef } from "react";

import {
  ApplicantWebcamEventPayload,
  ApplicantWebcamGetUploadUrl,
  postWebcamEvent,
} from "@api/applicants";

import { useStoreContext } from "@context";

import { dayjs } from "@shared/services/date";
import Logger from "@shared/services/logger";
import {
  formatWebcamFormData,
  getWebcamUploadUrls,
} from "@shared/services/webcam";

export const useSendWebcamConnectEvent = ({
  challengeResultId,
}: {
  challengeResultId: number;
}) => {
  const { applicantExamId } = useStoreContext();

  const mutation = useMutation({
    mutationFn: async ({
      eventType,
      mediaDeviceId,
      mediaDeviceLabel,
    }: Pick<
      ApplicantWebcamEventPayload,
      "eventType" | "mediaDeviceId" | "mediaDeviceLabel"
    >) =>
      postWebcamEvent({
        applicantExamId,
        payload: {
          eventType, // webcam-started, webcam-stopped, or webcam-unavailable
          challengeResultId,
          mediaDeviceId, // (optional) MediaDeviceInfo.deviceId, only populated for webcam-started events
          mediaDeviceLabel, // (optional) MediaDeviceInfo.label, only populated for webcam-started events
        },
      }),
  });

  return mutation;
};

export interface SendWebcamScreenshotParams {
  timestamp: string;
  imgDataUrl: string;
  applicantExamId: number;
  challengeResultId: number;
}

type UseMutationOptionsProps = UseMutationOptions<
  boolean,
  Error,
  SendWebcamScreenshotParams,
  unknown
>;

export const useSendWebcamScreenshot = ({
  onSuccess,
  onError,
}: UseMutationOptionsProps) => {
  const rollbar = useRollbar();

  const uploadUrlBatchRef = useRef<ApplicantWebcamGetUploadUrl | null>(null);

  const mutation = useMutation<boolean, Error, SendWebcamScreenshotParams>({
    mutationFn: async ({
      applicantExamId,
      challengeResultId,
      imgDataUrl,
      timestamp,
    }: SendWebcamScreenshotParams) => {
      let uploadUrlBatch = uploadUrlBatchRef.current;

      const isInvalidUrlBatch =
        !uploadUrlBatch ||
        !uploadUrlBatch?.uploadUrls.length ||
        (uploadUrlBatch?.expires &&
          dayjs().isAfter(dayjs(uploadUrlBatch?.expires)));

      if (isInvalidUrlBatch) {
        const newUploadUrlBatch = await getWebcamUploadUrls({
          applicantExamId,
        });
        uploadUrlBatch = newUploadUrlBatch;
        uploadUrlBatchRef.current = uploadUrlBatch;
      }

      const uploadItem = uploadUrlBatch?.uploadUrls.pop();

      if (!uploadItem) {
        Logger.error("No upload url found");
        throw new Error(`No upload url found`);
      }

      const { url } = uploadItem;

      const formData = formatWebcamFormData({
        uploadItem,
        timestamp,
        challengeResultId,
        imgDataUrl,
      });

      try {
        // s3 should return a 204 status code if the upload is a success.
        // but due to setting the mode to no-cors, javascript cannot access
        // the response https://stackoverflow.com/questions/36292537/what-is-an-opaque-response-and-what-purpose-does-it-serve
        await fetch(url, {
          mode: "no-cors",
          method: "POST",
          body: formData,
        });
      } catch (error) {
        Logger.error("Failed to upload to s3:", error);
        throw new Error(`Failed to upload webcam image to url ${url}`);
      }

      return true;
    },
    onSuccess,
    onError: (...error) => {
      Logger.error("[useSendWebcamScreenshot] error", error);
      onError?.(...error);
      rollbar.error(
        "[useSendWebcamScreenshot] Failed to send webcam screenshot",
        error,
      );
    },
  });

  return mutation;
};
